import {
  dataURItoBlob,
  deleteFilesFromDirectory,
  deleteWholeDirectory,
  getFilesFromDirectory,
  saveFilesIntoADirectory,
} from '../utils/files';
import type { LocalImage } from './offlineCompoundQueue';
import type { PhotoHandoff } from './photoHandoff';

/**
 * Dónde leen y escriben los guardados de actuación y accidente lo que el
 * operario rellenó (spec outbox-autocontenido, revisión tras el inventario).
 *
 * Antes lo hacían SIEMPRE sobre `sessionStorage` y las carpetas OPFS del
 * formulario, así que el reintento de la cola tenía que poner allí su foto y
 * pisaba el formulario abierto (medido en tablet: el formulario de B enseñaba
 * A). Ahora el almacén y la fuente de imágenes se inyectan: la pantalla usa los
 * de siempre y el reintento unos en memoria, construidos con los datos de la
 * propia operación, que no tocan nada de la pantalla.
 */
export interface FormStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
  /** Las claves presentes, para los bucles que recorren el almacén. */
  keys(): string[];
}

/** El almacén de la pantalla: `sessionStorage`, como siempre. */
export const screenFormStore: FormStore = {
  getItem: (key) => sessionStorage.getItem(key),
  setItem: (key, value) => sessionStorage.setItem(key, value),
  removeItem: (key) => sessionStorage.removeItem(key),
  keys: () => Object.keys(sessionStorage),
};

/**
 * Un almacén en memoria para el reintento. Empieza con la foto de la operación;
 * las claves vacías de la foto quedan AUSENTES (antes se saltaban y se leía lo
 * que hubiera en `sessionStorage`). Lo que el guardado escriba al terminar
 * (marcas, limpiezas) se queda aquí y se tira.
 */
export function createMemoryFormStore(initial: Record<string, string | null>): FormStore {
  const data = new Map<string, string>();
  Object.entries(initial).forEach(([key, value]) => {
    if (value !== null && value !== undefined) data.set(key, value);
  });
  return {
    getItem: (key) => data.get(key) ?? null,
    setItem: (key, value) => {
      data.set(key, String(value));
    },
    removeItem: (key) => {
      data.delete(key);
    },
    keys: () => [...data.keys()],
  };
}

/**
 * Las carpetas de fotos y croquis del formulario (`action`, `canvas-action`,
 * `accident`, `canvas-accident`). La carpeta propia de la cola de fotos
 * (`offline-pending-uploads`) NO pasa por aquí: es de la cola, no del formulario.
 */
export interface FormImageSource {
  list(directory: string): Promise<{ file: File; id: string }[]>;
  save(directory: string, files: File[]): Promise<void>;
  remove(directory: string, fileName: string): Promise<void>;
  clear(directory: string): Promise<void>;
}

/** Las carpetas OPFS de la pantalla, como siempre. */
export const screenImageSource: FormImageSource = {
  list: (directory) => getFilesFromDirectory(directory),
  save: (directory, files) => saveFilesIntoADirectory(directory, files),
  remove: (directory, fileName) => deleteFilesFromDirectory(directory, fileName),
  clear: (directory) => deleteWholeDirectory(directory),
};

/**
 * Las fotos de la operación, en memoria. Como OPFS, guardar no pisa un fichero
 * que ya esté con el mismo nombre.
 */
export function createMemoryImageSource(images: LocalImage[]): FormImageSource {
  const folders = new Map<string, File[]>();
  const folder = (directory: string): File[] => {
    if (!folders.has(directory)) folders.set(directory, []);
    return folders.get(directory) as File[];
  };
  for (const img of images) {
    try {
      folder(img.directory).push(
        new File([dataURItoBlob(img.base64Data)], img.fileName, { type: img.mimeType }),
      );
    } catch {
      console.warn(`[formStorage] Imagen ilegible, se salta: ${img.fileName}`);
    }
  }
  return {
    list: async (directory) => folder(directory).map((file) => ({ file, id: file.name })),
    save: async (directory, files) => {
      const current = folder(directory);
      files.forEach((file) => {
        if (!current.some((f) => f.name === file.name)) current.push(file);
      });
    },
    remove: async (directory, fileName) => {
      folders.set(directory, folder(directory).filter((f) => f.name !== fileName));
    },
    clear: async (directory) => {
      folders.set(directory, []);
    },
  };
}

/** Lo que recibe un guardado para saber dónde leer y escribir. */
export interface FormContext {
  store: FormStore;
  images: FormImageSource;
  /**
   * Solo en los reintentos de la cola compuesta: si la operación ya entregó sus
   * fotos a la bandeja, no se vuelven a entregar (ver `photoHandoff.ts`).
   */
  photoHandoff?: PhotoHandoff;
}

/** El de la pantalla: lo que usaban hasta ahora sin decirlo. */
export const screenFormContext: FormContext = {
  store: screenFormStore,
  images: screenImageSource,
};
